import { Link } from "react-router-dom";
import { ArrowRight, Shield, Sparkles, Stethoscope } from "lucide-react";
import Reveal from "@/components/site/Reveal";

/**
 * Editorial pricing teaser — Skandi magazine "menu card" style.
 * Statt generischer SaaS-Boxen: warme Cream-Karten mit Terra-Akzentstreifen,
 * eleganter Display-Preis, kleines "Am beliebtesten"-Editorial-Label.
 */
const TEASER_TIERS = [
  {
    id: "pflege-ki-kompakt",
    icon: Sparkles,
    kicker: "Für Pflegedienste bis 10 Mitarbeiter",
    name: "Pflege-KI Kompakt",
    price: "199 €",
    note: "/ Monat · netto",
    line: "1 PDL + bis zu 10 Sub-Accounts · alle Features · Beispiel-Mappe inklusive",
    featured: true,
  },
  {
    id: "pflege-ki-team",
    icon: Stethoscope,
    kicker: "Für Pflegedienste ab 11 Mitarbeiter",
    name: "Pflege-KI Team",
    price: "349 €",
    note: "/ Monat · netto",
    line: "Gleiche Features wie Kompakt · unbegrenzt Sub-Accounts · Onboarding-Call inklusive",
  },
];

export default function CampusPricingTeaser() {
  return (
    <section
      id="preise-teaser"
      data-testid="campus-pricing-teaser"
      className="bg-cream-50 py-28 md:py-40"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial section header · asymmetric */}
        <Reveal>
          <div className="grid md:grid-cols-12 gap-6 items-end mb-16">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Dickies-Campus · Preise
                </span>
              </div>
              <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
                Zwei klare Pakete.<br />
                <span className="text-terra">Ein Unterschied: die Teamgröße.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-sand-600 text-[15px] leading-[1.7] max-w-sm md:ml-auto md:text-right">
                Beide Pakete enthalten alle Funktionen — Patientenmappe, QPR-26,
                SIS-Diktat, PDF-Export. Du zahlst nur für die Größe deines Teams.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Editorial cards */}
        <div
          className="grid md:grid-cols-2 gap-x-6 md:gap-x-10 gap-y-8 max-w-4xl mx-auto"
          data-testid="campus-pricing-teaser-grid"
        >
          {TEASER_TIERS.map((t, idx) => {
            const Icon = t.icon;
            return (
              <Reveal key={t.id} delay={idx * 120}>
                <div
                  data-testid={`pricing-teaser-${t.id}`}
                  className={`relative h-full flex flex-col ${
                    t.featured
                      ? "bg-cream-100 border-t-[3px] border-terra"
                      : "bg-cream-50 border-t border-sand-300"
                  }`}
                  style={{
                    paddingTop: t.featured ? "2rem" : "2.25rem",
                    paddingBottom: "2.25rem",
                    paddingLeft: "1.75rem",
                    paddingRight: "1.75rem",
                  }}
                >
                  {/* Editorial kicker · handwritten italic feel */}
                  <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold text-terra mb-6">
                    {t.kicker}
                  </div>

                  {/* Name in display serif */}
                  <div className="font-magazine text-[1.75rem] md:text-[2rem] font-normal text-sand-800 leading-none">
                    {t.name}
                  </div>

                  {/* Price · asymmetric baseline */}
                  <div className="mt-8 flex items-baseline gap-2">
                    <span className="font-magazine text-[3.25rem] font-normal leading-none text-sand-800">
                      {t.price}
                    </span>
                    <span className="text-[13px] text-sand-500">{t.note}</span>
                  </div>

                  {/* Divider · warm sand line */}
                  <div className="mt-8 h-px w-full bg-sand-300/60" />

                  {/* Body copy */}
                  <p className="mt-6 text-[14.5px] text-sand-700 leading-[1.7] flex-1">
                    {t.line}
                  </p>

                  {/* Meta strip */}
                  <div className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-sand-500 font-medium">
                    <Icon className="h-3.5 w-3.5 text-terra" strokeWidth={2} />
                    Monatlich kündbar
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Editorial CTA block */}
        <Reveal delay={320}>
          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-sand-300/60 pt-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-terra mb-2">
                Vollständiger Vergleich
              </div>
              <div className="font-magazine text-[1.35rem] md:text-[1.5rem] text-sand-800 leading-tight max-w-xl">
                Alle Limits, Module &amp; der Halluzinationsschutz im Detail.
              </div>
            </div>
            <Link
              to="/preise"
              data-testid="campus-pricing-teaser-cta"
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-sand-800 border-b border-terra pb-1 hover:text-terra transition-colors whitespace-nowrap"
            >
              Tarife &amp; Preise ansehen
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={400}>
          <p className="mt-10 text-[11px] uppercase tracking-[0.22em] text-sand-500 font-medium">
            Alle Abo-Preise zzgl. gesetzl. USt. · monatlich kündbar
          </p>
        </Reveal>
      </div>
    </section>
  );
}
